import { useEffect } from 'react';
import Box from '@mui/material/Box';

import { SupportedTimeZones } from 'enums';
import {
  getReadableTimeZoneDateTime,
  getTimeZoneGTMWithCountry,
  timeZoneMappingService,
} from 'services';
import { useScreenWidth } from 'hooks';

import { StyledWorldMap } from './WorldMapRaw';
import { StyledMapTooltip } from './MapTooltip';
import {
  DATA_FOR_TOOLTIP_VALUE,
  DATA_TZ_NAME_KEY,
  WORLD_MAP_SVG_ID,
  NOT_MATCHED_TIME_ZONE_OPACITY_VALUE,
} from './consts';
import {
  calcOpacityValueBasedOnOffset,
  getTimeZoneNodes,
  getWorkingDayOverlapHoursCountInfo,
} from './utils';

import type { TimeZonesMap } from 'types';

interface WorldMapProps {
  sourceTimeZoneName: SupportedTimeZones | null;
  minutesOffset: number | null;
  workingDayLengthInMinutes: number | null;
  changeSelectedTz: (newValue: null | SupportedTimeZones) => unknown;
  matchedTzNamesWithOffset: TimeZonesMap | null;
  mapWrapperId: string;
}

export const WorldMapVisualization = ({
  sourceTimeZoneName,
  minutesOffset,
  changeSelectedTz,
  matchedTzNamesWithOffset,
  workingDayLengthInMinutes,
  mapWrapperId,
}: WorldMapProps) => {
  const { isMobile } = useScreenWidth();

  // if mobile we do not want to make the world map clickable
  useEffect(() => {
    const worldMapSvgWrapper = document.getElementById(
      WORLD_MAP_SVG_ID
    ) as HTMLElement;

    if (isMobile) {
      worldMapSvgWrapper.style.pointerEvents = 'none';
    } else {
      worldMapSvgWrapper.style.pointerEvents = 'unset';
    }
  }, [isMobile]);

  // this effect is responsible for dispatching to the parent clicked time zone from map
  useEffect(() => {
    const worldMapSvgWrapper = document.getElementById(
      WORLD_MAP_SVG_ID
    ) as HTMLElement;

    const clickHandler = (e: MouseEvent) => {
      const tzName = (e.target as HTMLElement)?.getAttribute(
        DATA_TZ_NAME_KEY
      ) as SupportedTimeZones;

      if (tzName) changeSelectedTz(tzName);
    };

    worldMapSvgWrapper.addEventListener('click', clickHandler);

    return () => {
      worldMapSvgWrapper.removeEventListener('click', clickHandler);
    };
  }, [changeSelectedTz]);

  // this effect is responsible for highlighting time zones based on offset range and selected time zone
  useEffect(() => {
    const timeZoneNodes = getTimeZoneNodes();

    if (
      minutesOffset !== null &&
      sourceTimeZoneName !== null &&
      matchedTzNamesWithOffset !== null
    ) {
      timeZoneNodes.forEach((timeZoneNode) => {
        const comparisonTimeZoneName = timeZoneNode.getAttribute(
          DATA_TZ_NAME_KEY
        ) as SupportedTimeZones;

        if (comparisonTimeZoneName.toLowerCase() in matchedTzNamesWithOffset) {
          timeZoneNode.classList.add('highlighted');
          timeZoneNode.style.opacity = calcOpacityValueBasedOnOffset(
            comparisonTimeZoneName,
            sourceTimeZoneName,
            matchedTzNamesWithOffset
          ).toString();
        } else {
          timeZoneNode.classList.remove('highlighted');
          timeZoneNode.style.opacity =
            NOT_MATCHED_TIME_ZONE_OPACITY_VALUE.toString();
        }
      });
    } else {
      timeZoneNodes.forEach((timeZoneNode) => {
        timeZoneNode.classList.remove('highlighted');
        timeZoneNode.style.opacity = 'unset';
      });
    }
  }, [sourceTimeZoneName, minutesOffset, matchedTzNamesWithOffset]);

  return (
    <Box id={mapWrapperId}>
      <StyledWorldMap sourceTimeZoneName={sourceTimeZoneName} />
      <StyledMapTooltip
        getContent={(content) => {
          const timeZoneName = content as SupportedTimeZones;
          const timeZoneData =
            timeZoneMappingService.mapCorrectTimeZoneDataToDeprecated(
              timeZoneName
            );

          return (
            <>
              {getTimeZoneGTMWithCountry(timeZoneName)}
              <br />
              {getReadableTimeZoneDateTime(timeZoneData)}
              <br />
              {getWorkingDayOverlapHoursCountInfo({
                timeZoneName,
                matchedTzNamesWithOffset,
                workingDayLengthInMinutes,
              })}
            </>
          );
        }}
        delayShow={10}
        id={DATA_FOR_TOOLTIP_VALUE}
        type="dark"
        place="top"
        effect="float"
        arrowColor="transparent"
      />
    </Box>
  );
};
